import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import consultantsImage from '../assets/images/Image 4_Consultants.png';
import SEO from '../components/SEO';

const styles = `
  .feature-card {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: all 0.3s ease;
  }
  .feature-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
  .feature-icon {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  .learn-more-text {
    color: #2563eb;
    font-weight: 500;
    margin-top: 1rem;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  .feature-card:hover .learn-more-text {
    opacity: 1;
  }
  .pulse-glow {
    animation: pulse 2s infinite;
  }
  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.4); }
    70% { box-shadow: 0 0 0 10px rgba(37, 99, 235, 0); }
    100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
  }
`;

const tips = [
  {
    icon: "⏱️",
    title: "Time Management",
    text: "Practice managing your time effectively - you have about 2 minutes per question in the assessment."
  },
  {
    icon: "📊",
    title: "Data Interpretation",
    text: "Focus on understanding charts, graphs, and data visualization - they're crucial for all three games."
  },
  {
    icon: "🎯",
    title: "Strategic Thinking",
    text: "Develop a systematic approach to problem-solving and decision-making under time pressure."
  },
  {
    icon: "📝",
    title: "Practice Makes Perfect",
    text: "Regular practice with similar scenarios will help you build confidence and improve your performance."
  },
  {
    icon: "🧮",
    title: "Mental Math",
    text: "Sharpen your mental math skills - you'll need to make quick calculations without a calculator."
  },
  {
    icon: "🎮",
    title: "Game Mechanics",
    text: "Familiarize yourself with the game mechanics and interface before the actual assessment."
  }
];

const pitfalls = [
  {
    icon: "❌",
    title: "Rushing Through",
    text: "Don't rush through questions without fully understanding the scenario and requirements."
  },
  {
    icon: "⚠️",
    title: "Ignoring Instructions",
    text: "Always read instructions carefully - they contain crucial information for solving the problems."
  },
  {
    icon: "🔄",
    title: "No Strategy",
    text: "Going in without a clear strategy can lead to poor time management and decision-making."
  },
  {
    icon: "📉",
    title: "Poor Data Analysis",
    text: "Failing to properly analyze data and charts can lead to incorrect conclusions and answers."
  }
];

const whyCognidoor = [
  {
    icon: "🧮",
    title: "Interactive Solvers",
    text: "Get instant solutions with our advanced solvers for Ecosystem Building, Red Rock Study, and Sea Wolf Game.",
    link: "/ecosystem-solver"
  },
  {
    icon: "📚",
    title: "Comprehensive Guides",
    text: "Access detailed guides and tutorials for all three McKinsey Solve games with expert strategies.",
    link: "/consulting-math-guides"
  },
  {
    icon: "🎯",
    title: "Practice Cases",
    text: "Master 16+ realistic practice cases covering all difficulty levels and game types.",
    link: "/redrock"
  },
  {
    icon: "⚡",
    title: "Fast Solutions",
    text: "Get instant results with our manual entry and OCR upload capabilities for quick problem-solving.",
    link: "/seawolf"
  },
  {
    icon: "💡",
    title: "Expert Tips",
    text: "Learn proven strategies and time management techniques from successful McKinsey candidates.",
    link: "/about-solve"
  },
  {
    icon: "🔓",
    title: "Flexible Access",
    text: "Choose between Basic and Pro tiers to access the features that match your preparation needs.",
    link: "/"
  }
];

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

const ctaPrimary =
  'inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-blue-600 to-blue-400 text-white shadow-sm hover:shadow-md transition-all';
const ctaSecondary =
  'inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold border-2 border-blue-600 text-blue-700 bg-white hover:bg-blue-50 transition-colors';
const ctaMuted =
  'inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-medium border border-slate-200 text-slate-700 bg-white hover:border-slate-300 hover:bg-slate-50 transition-colors';
const ctaExternal =
  'inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-medium border border-slate-300 text-slate-600 bg-slate-50 hover:bg-slate-100 transition-colors';

function McKinseySolveCtaStrip() {
  return (
    <section
      className="relative z-20 bg-gradient-to-b from-slate-50 to-white border-y border-slate-200/80 py-6 sm:py-8 px-4"
      aria-label="Quick links for McKinsey Solve prep"
    >
      <p className="text-center text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
        Practice on Cognidoor
      </p>
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3 max-w-4xl mx-auto">
        <Link to="/mckinsey-ai-coach" className={ctaPrimary}>
          McKinsey AI Coach
        </Link>
        <Link to="/seawolf-practice" className={ctaSecondary}>
          Sea Wolf practice
        </Link>
        <Link to="/redrock" className={ctaSecondary}>
          Red Rock simulation
        </Link>
        <Link to="/ecosystem-solver" className={ctaSecondary}>
          Ecosystem solver
        </Link>
        <Link to="/redrock-guide" className={ctaMuted}>
          Free Red Rock drills
        </Link>
        <Link to="/about" className={ctaMuted}>
          About Cognidoor
        </Link>
        <Link to="/about-bcg-casey" className={ctaMuted}>
          About BCG Casey
        </Link>
      </div>
      <p className="text-center text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 mt-8 mb-3">
        Official McKinsey
      </p>
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3 max-w-3xl mx-auto">
        <a
          href="https://www.mckinsey.com/careers/how-we-hire"
          target="_blank"
          rel="noopener noreferrer"
          className={ctaExternal}
        >
          How we hire &amp; assessments
        </a>
        <a
          href="https://www.mckinsey.com/careers"
          target="_blank"
          rel="noopener noreferrer"
          className={ctaExternal}
        >
          McKinsey careers
        </a>
      </div>
    </section>
  );
}

export default function AboutSolvePage() {
  const navigate = useNavigate();

  const handleCardClick = (link) => {
    if (link) {
      navigate(link);
    }
  };

  return (
    <div className="bg-white text-gray-900 font-sans">
      <SEO
        title="About McKinsey Solve | Cognidoor"
        description="Overview of the McKinsey Solve assessment — format, games, scoring, and how to prepare effectively."
        url="/about-solve"
        noIndex
      />
      {/* Hero Section */}
      <section className="relative z-30 bg-gradient-to-br from-blue-100 via-blue-50 to-white pb-6 sm:pb-12 min-h-[320px] sm:min-h-[500px] flex items-center overflow-visible">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-gray-800/85 to-gray-700/80">
          <div className="absolute inset-0" style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'overlay'
          }} />
        </div>
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="absolute z-10 hidden md:block hero-image w-[200px] md:w-[300px] lg:w-[400px] aspect-[2/3]"
          style={{ right: '2vw', top: '24px' }}
        >
          <div className="h-full rounded-xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
            <img 
              src={consultantsImage} 
              alt="Consultants" 
              className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
            />
          </div>
        </motion.div>
        <div className="container pl-3 sm:pl-[36px] md:pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 py-10 sm:py-24 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl hero-content"
          >
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 leading-tight text-white">
              About McKinsey Solve
            </h1>
            <p className="text-base sm:text-xl md:text-2xl text-white/90 mb-6 sm:mb-10 leading-relaxed">
              McKinsey Solve is a 110-minute digital assessment that tests your cognitive and problem-solving skills through interactive, gamified scenarios. Learn how to master it below!
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/consulting-math-guides')}
                className="group relative px-5 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-blue-600 to-blue-400 text-white text-base sm:text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
              >
                Master Math &amp; Chart Skills
                <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
              </motion.button>
              <Link
                to="/mckinsey-ai-coach"
                className="inline-flex items-center justify-center px-5 py-3 sm:px-8 sm:py-4 border-2 border-white/70 text-white text-base sm:text-lg font-semibold rounded-xl hover:bg-white/10 transition-all"
              >
                McKinsey AI Coach
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <McKinseySolveCtaStrip />

      {/* Assessment Structure */}
      <section className="py-10 sm:py-20 bg-gradient-to-b from-white via-blue-50/30 to-white text-center px-3 sm:px-6 md:px-10 relative">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-12 section-title tracking-tight">Assessment Structure</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8 max-w-7xl mx-auto">
          <motion.div 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/ecosystem-solver')}
            className="feature-card rounded-2xl shadow-md p-4 sm:p-6 md:p-8 backdrop-blur-sm cursor-pointer"
          >
            <div className="feature-icon">🌱</div>
            <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-800">Ecosystem Building</h3>
            <p className="text-sm text-gray-600 leading-relaxed">Create a balanced and sustainable ecosystem by carefully selecting species based on their food chains, calorie requirements, and interdependencies.</p>
            <div className="learn-more-text">Learn More</div>
          </motion.div>
          <motion.div 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/redrock')}
            className="feature-card rounded-2xl shadow-md p-4 sm:p-6 md:p-8 backdrop-blur-sm cursor-pointer"
          >
            <div className="feature-icon">🏔️</div>
            <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-800">Redrock Study</h3>
            <p className="text-sm text-gray-600 leading-relaxed">Analyze ecological or situational data from a fictional island, making strategic decisions based on interpreting complex datasets and visuals.</p>
            <div className="learn-more-text">Learn More</div>
          </motion.div>
          <motion.div 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/seawolf')}
            className="feature-card rounded-2xl shadow-md p-4 sm:p-6 md:p-8 backdrop-blur-sm cursor-pointer"
          >
            <div className="feature-icon">🌊</div>
            <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-800">Sea Wolf Game</h3>
            <p className="text-sm text-gray-600 leading-relaxed">Manage marine ecosystems, evaluating interactions among aquatic species and optimizing resources for sustainable outcomes.</p>
            <div className="learn-more-text">Learn More</div>
          </motion.div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-10 sm:py-20 bg-gradient-to-b from-white via-blue-50/20 to-white px-3 sm:px-6 md:px-10 text-center relative">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 section-title tracking-tight">Best Tips for Acing McKinsey Solve</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 max-w-7xl mx-auto">
          {tips.map((tip, idx) => (
            <div key={idx} className="feature-card rounded-2xl shadow-md p-4 sm:p-6 md:p-8 backdrop-blur-sm" style={{ animationDelay: `${idx * 0.1}s` }}>
              <div className="feature-icon">{tip.icon}</div>
              <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-800">{tip.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{tip.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pitfalls Section */}
      <section className="py-10 sm:py-20 bg-gradient-to-b from-white via-blue-50/30 to-white text-center px-3 sm:px-6 md:px-10 relative">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 section-title tracking-tight">Common Pitfalls & How to Avoid Them</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 max-w-7xl mx-auto">
          {pitfalls.map((pit, idx) => (
            <div key={idx} className="feature-card rounded-2xl shadow-md p-4 sm:p-6 md:p-8 backdrop-blur-sm" style={{ animationDelay: `${idx * 0.1}s` }}>
              <div className="feature-icon">{pit.icon}</div>
              <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-800">{pit.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{pit.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Cognidoor Section */}
      <section className="py-10 sm:py-20 bg-gradient-to-b from-white via-blue-50/20 to-white px-3 sm:px-6 md:px-10 text-center relative">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 section-title tracking-tight">Why Cognidoor is the Best Tool for McKinsey Solve</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 max-w-7xl mx-auto">
          {whyCognidoor.map((item, idx) => (
            <motion.div 
              key={idx} 
              className={`feature-card rounded-2xl shadow-md p-4 sm:p-6 md:p-8 backdrop-blur-sm cursor-pointer ${item.link ? 'pulse-glow' : ''}`}
              style={{ animationDelay: `${idx * 0.1}s` }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleCardClick(item.link)}
            >
              <div className="feature-icon">{item.icon}</div>
              <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-800">{item.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <McKinseySolveCtaStrip />
    </div>
  );
}
